/**
 * Flow Registry Manager
 *
 * Central registry that maps application types AND intents to their corresponding
 * wizard flow definitions. This enables different wizard paths based on
 * project type OR user intent while maintaining clean separation of concerns.
 */

// Flow definitions
import iotFlow from './iotFlow';
import migrateFlow from './migrateFlow';
// Note: Default API/WEB flows will be extracted to this registry pattern in future
// For now, they use legacy hardcoded routing in OnboardingWizard.js

/**
 * Application Type Flow Registry
 * Maps application type to flow definition
 */
const appTypeFlowRegistry = {
  // api: apiFlow,  // TODO: Extract from OnboardingWizard.js
  // web: webFlow,  // TODO: Extract from OnboardingWizard.js
  // mobile: webFlow,  // Shares WEB flow
  iot_hardware: iotFlow  // IoT/Hardware flow
};

/**
 * Intent Flow Registry
 * Maps intent to flow definition (takes precedence over app type)
 */
const intentFlowRegistry = {
  MIGRATE_SCRIPTS: migrateFlow  // MIGRATE existing scripts flow
  // MANUAL_TO_AUTOMATION: uses legacy routing
  // JIRA_REQUIREMENTS: uses legacy routing
  // SAMPLE_PROJECT: uses legacy routing
};

/**
 * Get flow definition by application type
 * @param {string} appType - Application type (api, web, mobile, iot_hardware)
 * @returns {Object} Flow definition
 */
export const getFlow = (appType) => {
  const normalizedType = appType?.toLowerCase();
  return appTypeFlowRegistry[normalizedType] || null;
};

/**
 * Get flow definition by intent
 * @param {string} intent - User intent (MIGRATE_SCRIPTS, etc.)
 * @returns {Object} Flow definition
 */
export const getFlowByIntent = (intent) => {
  return intentFlowRegistry[intent] || null;
};

/**
 * Get flow definition from session context
 * Priority: Intent-based flow > App-type-based flow > null
 * @param {Object} session - Session object with intent and project information
 * @returns {Object} Flow definition
 */
export const getFlowForSession = (session) => {
  // Check for intent-based flow first (higher priority)
  if (session?.intent) {
    const intentFlow = getFlowByIntent(session.intent);
    if (intentFlow) {
      return intentFlow;
    }
  }
  
  // Fall back to app-type-based flow
  const appType = session?.project?.applicationType || 'api';
  return getFlow(appType);
};

/**
 * Check if a flow is registered (app type or intent)
 * @param {string} key - Application type or intent to check
 * @returns {boolean} True if flow is registered
 */
export const hasFlow = (key) => {
  const normalizedType = key?.toLowerCase();
  return appTypeFlowRegistry.hasOwnProperty(normalizedType) ||
         intentFlowRegistry.hasOwnProperty(key);
};

/**
 * Get all registered flow IDs (both app types and intents)
 * @returns {Object} Object with appTypes and intents arrays
 */
export const getRegisteredFlows = () => {
  return {
    appTypes: Object.keys(appTypeFlowRegistry),
    intents: Object.keys(intentFlowRegistry)
  };
};

export default {
  appTypes: appTypeFlowRegistry,
  intents: intentFlowRegistry
};